<template>
    <v-app-bar color="typography3">
        <v-app-bar-nav-icon v-if="mobile" @click="drawer = true" />
        <div class="d-flex align-center">
            <v-btn to="/" variant="text" class="home text-typography2">
                <span class="me-2 text-typography2 text-title-medium"
                    >ARUP</span
                >
                <span class="me-2 text-typography1 text-body-large"
                    >COMPUTE</span
                >
            </v-btn>
        </div>
        <Search v-if="user && !xs" />
        <v-spacer></v-spacer>
        <span v-if="user && !mobile">
            <v-btn
                v-if="!isExternalFacing"
                to="/about"
                class="text-capitalize me-2 text-typography2 text-title-small"
                :style="[lg && { padding: '10px!important' }]"
                variant="text"
            >
                About
            </v-btn>

            <v-btn
                to="/library"
                class="text-capitalize me-2 text-typography2 text-title-small"
                :style="[lg && { padding: '10px!important' }]"
                variant="text"
            >
                Libraries
            </v-btn>

            <v-tooltip
                v-model="show"
                location="bottom"
                v-if="showDesktopPrompt && !isExternalFacing"
            >
                <template v-slot:activator="{ props }">
                    <v-btn
                        to="/integrations"
                        class="
                            text-capitalize
                            me-2
                            text-typography2 text-title-small
                        "
                        :style="[
                            lg && {
                                padding: '10px!important',
                            },
                        ]"
                        variant="text"
                        v-bind="props"
                    >
                        <v-badge
                            bordered
                            color="accent"
                            icon="mdi-exclamation-thick"
                            >Integrations</v-badge
                        >
                    </v-btn>
                </template>
                <div style="text-align: center">
                    Seems like you haven't used the desktop<br />clients yet,
                    download them here!
                </div>
            </v-tooltip>
            <v-btn
                v-else-if="!isExternalFacing"
                to="/integrations"
                class="text-capitalize me-2 text-typography2 text-title-small"
                :style="[lg && { padding: '10px!important' }]"
                variant="text"
            >
                Integrations
            </v-btn>

            <v-btn
                v-if="!isExternalFacing"
                to="/docs"
                class="text-capitalize me-2 text-typography2 text-title-small"
                :style="[lg && { padding: '10px!important' }]"
                variant="text"
                target="_blank"
            >
                Documentation
                <v-icon size="small" class="ms-4">mdi-open-in-new</v-icon>
            </v-btn>

            <v-btn
                v-if="!isExternalFacing && showStats"
                to="/stats"
                class="text-capitalize me-2 text-typography2 text-title-small"
                :style="[lg && { padding: '10px!important' }]"
                variant="text"
            >
                Statistics
            </v-btn>

            <v-menu open-on-hover offset-y>
                <template #activator="{ props }">
                    <v-btn
                        v-bind="props"
                        variant="text"
                        class="
                            text-capitalize
                            me-2
                            text-typography2 text-title-small
                            short-padding
                        "
                    >
                        Contact Us
                    </v-btn>
                </template>

                <v-list density="compact">
                    <v-list-item
                        v-for="(item, index) in getDropdownItems"
                        :key="index"
                        :href="item.href"
                        target="_blank"
                        :style="[{ width: '180px' }]"
                    >
                        <div
                            class="
                                d-flex
                                align-center
                                justify-space-between
                                w-100
                                text-no-wrap
                            "
                        >
                            <!-- Title -->
                            <span
                                class="
                                    dropdown-item
                                    text-capitalize
                                    text-typography2
                                    text-title-small
                                    text-no-wrap
                                "
                            >
                                {{ item.text }}
                            </span>

                            <!-- Icon -->
                            <v-icon
                                size="x-small"
                                class="ms-2 text-typography2"
                            >
                                mdi-open-in-new
                            </v-icon>
                        </div>
                    </v-list-item>
                </v-list>
            </v-menu>
        </span>
        <Login />
    </v-app-bar>
    <v-navigation-drawer v-model="drawer" absolute temporary>
        <v-list nav density="compact">
            <v-list-item to="/" prepend-icon="mdi-home" title="Home" />
            <v-list-item
                to="/about"
                prepend-icon="mdi-information"
                title="About"
            />
            <v-list-item
                to="/library"
                prepend-icon="mdi-calculator-variant"
                title="Libraries"
            />
            <v-list-item
                to="/integrations"
                prepend-icon="mdi-hubspot"
                title="Integrations"
            />
            <v-list-item
                to="/docs"
                target="_blank"
                prepend-icon="mdi-file-document"
            >
                <v-list-item-title>
                    Documentation
                    <v-icon size="small" class="ms-4">mdi-open-in-new</v-icon>
                </v-list-item-title>
            </v-list-item>
            <v-list-item
                v-if="showStats"
                to="/stats"
                prepend-icon="mdi-chart-bar"
                title="Statistics"
            />
            <v-list-group value="contact">
                <template #activator="{ props }">
                    <v-list-item
                        v-bind="props"
                        prepend-icon="mdi-comment-quote"
                        title="Contact Us"
                    />
                </template>

                <v-list-item
                    v-for="item in getDropdownItems"
                    :key="item.href"
                    :href="item.href"
                    target="_blank"
                >
                    <template #title>
                        {{ item.text }}
                    </template>

                    <template #append>
                        <v-icon size="small">mdi-open-in-new</v-icon>
                    </template>
                </v-list-item>
            </v-list-group>
        </v-list>
    </v-navigation-drawer>
</template>

<script setup>
import Login from './Login.vue'
import Search from './SearchBar.vue'
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useDisplay } from 'vuetify'

const store = useStore()
const { mobile, xs, lg } = useDisplay()
const drawer = ref(false)
const show = ref(false)

const user = computed(() => store.state.user)
const admins = computed(() => store.state.admins)
const isExternalFacing = computed(() => store.state.isExternalFacing)
const showStats = computed(() => store.state.showStats)

const showDesktopPrompt = computed(() => {
    // disabled feature - enable by removing the early return below
    return false
    // return user.value?.hasUsedDesktop
})

const feedbackLink = computed(() => {
    if (isExternalFacing.value)
        return `mailto:${admins.value.join(';')}?subject=ArupCompute%20Feedback`
    return 'https://feedback.arup.com/arupcompute/21'
})

const getDropdownItems = computed(() => {
    const items = [{ text: 'Give Feedback', href: feedbackLink.value }]
    if (!isExternalFacing.value) {
        items.push({
            text: 'Join MS Team',
            href: 'https://teams.microsoft.com/l/team/19%3a6e2e73116e964eb3add4570211127fa7%40thread.skype/conversations?groupId=180d4a41-ef12-4de6-bfff-2e979c3ee2f7&tenantId=4ae48b41-0137-4599-8661-fc641fe77bea',
        })
    }
    return items
})
</script>

<style scoped>
.home.v-btn__overlay {
    display: none;
}

.home.v-btn--active .v-btn__overlay {
    opacity: 0;
}

.dropdown-item {
    font-size: 0.875rem;
    letter-spacing: 0.05rem;
    font-weight: 500;
}

.v-toolbar__content {
    padding: 4px 16px !important;
}

.v-btn {
    letter-spacing: 0.0892857143em !important;
}

.short-padding.v-btn {
    padding: 0px 12px !important;
}
</style>
